<template>
  <base-label
    :class="labelClass"
    class="relative mb-4 text-sm font-semibold md:mb-6 md:text-base md:font-medium"
    :for="name"
  >
    {{ label }}
    <span v-if="props.showAsterisk" class="text-red-600">*</span>
    <select
      :name="name"
      :value="modelValue"
      :autocomplete="autocomplete ? 'on' : 'off'"
      v-bind="$attrs"
      class="mt-2 block !h-[50px] w-full bg-white text-sm font-normal focus:border-[1px] focus:border-black focus:outline-none md:mt-4 md:text-base md:font-medium"
      :class="{
        'text-gray-400': modelValue === null && !v$.value.$dirty,
        '!border-[1px] border-red-600 text-red-600':
          !!v$.value.$errors.length && v$.value.$dirty,
        'border-[1px] border-lightgray': borderAlwaysOn,
      }"
      @input="handleCustomInput(($event?.target as HTMLSelectElement).value)"
    >
      <option :disabled="!allowEmpty" value>
        {{
          !!v$.value.$errors.length
            ? v$.value.$errors[0]?.$message
            : placeholder
        }}
      </option>
      <option
        v-for="option in countryCodeOptions"
        :key="option.code"
        :value="option.code"
      >
        {{ option.label }}
      </option>
    </select>
    <icon-caret
      class="absolute bottom-4 right-[18px] w-[10px] text-[#999] transition-transform duration-200"
    />
  </base-label>
</template>

<script setup lang="ts">
import IconCaret from '@/components/icons/IconCaret.vue';
import useValidations, { ModelValue } from '@/utils/setup/useValidations';
import { customList } from 'country-codes-list';
import { computed } from 'vue';

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    allowEmpty?: boolean
    autocomplete?: boolean
    modelValue?: string | null
    label?: string
    labelClass?: string | string[]
    required?: boolean
    placeholder?: string
    borderAlwaysOn?: boolean
    showAsterisk?: boolean
  }>(),
  {
    allowEmpty: true,
    autocomplete: false,
    placeholder: 'Select',
    required: false,
    modelValue: null,
    borderAlwaysOn: false,
    showAsterisk: false,
    label: 'Country Code',
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: ModelValue): void
}>()

const { handleCustomInput, v$ } = useValidations(props, emit)

const countryCodesData = customList(
  'countryCallingCode',
  '{countryCallingCode}'
)

const countryCodeOptions = computed(() => {
  const dialCodes = new Set<string>()

  for (const callingCode of Object.keys(countryCodesData)) {
    dialCodes.add(`+${callingCode}`)
  }

  return Array.from(dialCodes)
    .map((code) => ({ code, label: code }))
    .sort((a, b) => {
      const numA = parseInt(a.code.replace('+', ''), 10)
      const numB = parseInt(b.code.replace('+', ''), 10)
      return numA - numB
    })
})

const name = computed(() => props.label?.toLowerCase().replace(/\s+/g, '-'))
</script>

<style lang="scss" scoped>
select {
  &:focus {
    + svg {
      @apply rotate-180;
    }
  }
}
</style>
